import React, { useMemo } from 'react';
import { ConsumptionTax } from '@/contexts/dynamic-config';
import {
  displayCurrencyPlaceholder,
  formatCurrency,
  roundMicrosToNearestWholeCent,
} from '@/utils/money';
import { InvoiceListItem } from './invoice-list-item';
import { ItemSubtitle } from '@/components/item-subtitle';
import { Props as ListItemProps, ListOption } from '@/components/list-item';
import { LOCALIZED_PACK_UNITS } from '@/utils/i18n';
import { OrderInvoiceLineItemFieldsFragment } from '@/types';
import { OrderItemListItemType } from '@/components/order-item-list';
import { PackUnit } from '@/enums';
import {
  RecyclerList,
  Props as RecyclerListProps,
} from '@/components/recycler-list';
import { ScrollViewProps } from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useUserContext } from '@/contexts/user-context';

interface InvoiceLineItemMetadata {
  itemCode: string;
  itemName: string | null;
  quantity: number;
  unit: string;
  unitPriceInMicros: number;
}

const getInvoiceLineItemMetadata = (
  option: OrderInvoiceLineItemFieldsFragment,
  consumptionTax: ConsumptionTax[] | null,
): InvoiceLineItemMetadata => {
  const unit_tax_in_micros =
    consumptionTax && option.unit_tax_in_micros ? option.unit_tax_in_micros : 0;
  return {
    itemCode: option.item_code,
    itemName: option.item_name || null,
    quantity: option.ship_quantity,
    unit: option.ship_erp_unit_of_measure,
    unitPriceInMicros: option.unit_price_in_micros + unit_tax_in_micros,
  };
};

type InvoiceDetailItemOption = ListOption<InvoiceLineItemMetadata>;

interface Props {
  displayItemPrices?: boolean;
  invoiceLineItems: OrderInvoiceLineItemFieldsFragment[];
  ListEmptyComponent?: RecyclerListProps<OrderItemListItemType>['ListEmptyComponent'];
  scrollViewProps?: ScrollViewProps;
}

export const InvoiceDetailList: React.FC<Props> = ({
  displayItemPrices = true,
  invoiceLineItems,
  ListEmptyComponent,
  scrollViewProps,
}) => {
  const { currencyCode } = usePaymentsContext();
  const { isLargeLayout } = useLayoutContext();
  const {
    selectedRestaurantRelationship: { consumptionTax },
  } = useUserContext();
  const allowedPackUnits = useMemo(() => Object.values(PackUnit), []);

  const sections = useMemo(() => {
    return [
      {
        data: invoiceLineItems.map<InvoiceDetailItemOption>((li) => {
          const metadata = getInvoiceLineItemMetadata(li, consumptionTax);
          return {
            metadata,
            title:
              // Use itemCode in place for missing itemName on large screens
              metadata.itemName || (isLargeLayout ? metadata.itemCode : ''),
            value: metadata.itemCode,
          };
        }),
        key: null,
      },
    ];
  }, [invoiceLineItems, isLargeLayout, consumptionTax]);

  const listItemProps: Partial<ListItemProps<InvoiceDetailItemOption>> = {
    renderMainContent: (option) => {
      const metadata = option.metadata!;
      const maybePackUnit = metadata.unit as PackUnit;
      const displayString = (
        allowedPackUnits.includes(maybePackUnit)
          ? LOCALIZED_PACK_UNITS[maybePackUnit]
          : metadata.unit
      ).toUpperCase();
      return (
        <InvoiceListItem
          formattedPrice={
            displayItemPrices
              ? formatCurrency(
                  roundMicrosToNearestWholeCent(
                    metadata.unitPriceInMicros * metadata.quantity,
                  ),
                  currencyCode,
                )
              : displayCurrencyPlaceholder(currencyCode)
          }
          quantity={metadata.quantity}
          subtitle={
            <ItemSubtitle
              currencyCode={currencyCode}
              displayString={displayString}
              showInlineModifiers
              sku={metadata.itemCode}
              unitCount={1}
              unitPriceInMicros={
                displayItemPrices ? metadata.unitPriceInMicros : null
              }
            />
          }
          title={option.title}
        />
      );
    },
  };

  return (
    <RecyclerList
      estimatedItemSize={[140, 120, 120, 90]}
      ListEmptyComponent={ListEmptyComponent}
      listItemProps={listItemProps}
      sections={sections}
      testID="invoice-detail-list"
      {...scrollViewProps}
    />
  );
};
